/* The ritual films: each one plays only while it is on screen.

   Behaviour carried over from the archived bundle-how-to-use section, which is
   where these four films lived before. Kept because it is the correct answer to
   "looping where they sit": four autoplaying videos would fetch four files the
   moment the page loads, and this page currently ships none.

   Three refusals, each deliberate (each about playing unprompted; a tap on a
   film is the reader asking, see below):
   - prefers-reduced-motion: never autoplay. The poster is the whole experience.
   - navigator.connection.saveData: never autoplay. Four films on a metered
     connection is a cost the reader did not agree to.
   - No IntersectionObserver: never autoplay. The poster still shows, so the page
     is complete without this file. Nothing here is load-bearing for meaning.

   play() returns a promise that rejects if the browser blocks playback. That
   rejection is expected and swallowed; an unhandled one prints a console error,
   and a console error is a thing someone will chase for an hour one day.  */
(function () {
  'use strict';

  var root = document.querySelector('[data-brf]');
  if (!root) return;

  var videos = root.querySelectorAll('video');
  if (!videos.length) return;

  var stillOnly =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    (navigator.connection && navigator.connection.saveData === true);
  var auto = !stillOnly && 'IntersectionObserver' in window;

  /* 2026-10-30 · each film answers a tap: hold it, or let it run. The refusals
     above still decide what plays UNPROMPTED; a tap is the reader asking, so a
     held film plays even under reduced motion or Save-Data. A held film stays
     held when it scrolls away and back. A play() the browser refuses (iOS Low
     Power Mode blocks muted autoplay) marks the film held, so the badge offers
     the tap that will work instead of claiming a film that is not running.

     2026-10-31 · what the button and badge SAY comes from the film itself, not
     from the intent: aria-pressed is true, and the badge shows the muted mark,
     only between `playing` and the next `pause`, `ended` or `error`. It used to
     follow _brfHeld, so all four films announced "pressed" at load, off screen
     and before a frame had arrived, and stayed pressed when play() was aborted
     or the file failed to decode (review). _brfHeld is still the intent: it
     decides whether the observer may restart a film. A tap answers what the
     button says: a playing film is held, anything else is asked to play. */
  function hold(v, held) {
    v._brfHeld = held;
  }

  function show(v, running) {
    var frame = v.closest('.brf-frame');
    var btn = frame && frame.querySelector('.brf-hold');
    if (!btn) return;
    v._brfRunning = running;
    frame.classList.toggle('is-held', !running);
    btn.setAttribute('aria-pressed', running ? 'true' : 'false');
  }

  function play(v) {
    // preload="none" means the first play() is also the first fetch.
    var p = v.play();
    if (p && typeof p.catch === 'function') p.catch(function (e) {
      // only a refusal holds the film; an AbortError is just a pause that won the race
      if (e && e.name === 'NotAllowedError') { hold(v, true); v._brfRefused = true; }
    });
  }

  /* Watches every film, not only autoplaying ones: a film a reduced-motion or
     Save-Data reader tapped into motion stops when it leaves the screen, and is
     then held, so it does not loop unseen and the badge stays true (review,
     2026-10-30). Only autoplay restarts a film on its way back in. */
  var observer = 'IntersectionObserver' in window && new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        v._brfOn = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (auto && !v._brfHeld) play(v);
        } else if (!v.paused) {
          v.pause();
          if (!auto) hold(v, true);
        }
      });
    },
    // Half the frame has to be showing. A film clipped at the edge of the
    // viewport is not something anyone is watching.
    { threshold: 0.5 }
  );

  /* 2026-11-01 · a refusal is not the visitor's choice. iOS Low Power Mode refuses every
     unprompted play(), so these films used to wait for a tap on each film itself, while
     every other film on the site starts on the visitor's next touch anywhere
     (ss-video-visibility.js; Sho, 2026-09-14: when autoplay fails, make it play). A film
     the browser refused, and the visitor has not held, is asked again from inside the next
     gesture, if it is on screen. A film the visitor held with its button stays held: the
     button clears the refusal mark. Proof: tests/browser/lpm-refused-autoplay-proof.mjs. */
  if (auto) {
    var retry = function (e) {
      var t = e && e.target;
      Array.prototype.forEach.call(videos, function (v) {
        // a gesture on this film's own frame is the button's to answer, never both
        var frame = v.closest('.brf-frame');
        if (t && frame && t.nodeType === 1 && frame.contains(t)) return;
        if (v._brfRefused && v._brfOn && v.paused) { v._brfRefused = false; hold(v, false); play(v); }
      });
    };
    ['touchend', 'pointerdown', 'click', 'keydown'].forEach(function (n) {
      document.addEventListener(n, retry, { passive: true, capture: true });
    });
  }

  Array.prototype.forEach.call(videos, function (v) {
    v.muted = true;          // a muted video is the only kind a browser will play unprompted
    v.loop = true;
    v.playsInline = true;
    v.removeAttribute('autoplay');
    /* The still shows until there is a real frame to show instead. `playing`
       is the primary signal; `timeupdate` and an already-playing check cover
       the paths where mobile Safari never fires it (the same trio the homepage
       hero's revealVideo uses — see sections/hero-section.liquid). */
    var goLive = function () { v.classList.add('is-live'); };
    v.addEventListener('playing', goLive, { once: true });
    v.addEventListener('timeupdate', goLive, { once: true });
    if (!v.paused && !v.ended && v.currentTime > 0) goLive();
    if (observer) observer.observe(v);

    var btn = v.closest('.brf-frame') && v.closest('.brf-frame').querySelector('.brf-hold');
    if (!btn) return;
    hold(v, !auto);
    show(v, !v.paused && !v.ended && v.readyState > 2);
    v.addEventListener('playing', function () { show(v, true); });
    // mobile Safari can play without `playing` (see goLive above): a frame advancing counts
    v.addEventListener('timeupdate', function () { if (!v.paused && !v._brfRunning) show(v, true); });
    ['pause', 'ended', 'error'].forEach(function (type) {
      v.addEventListener(type, function () { show(v, false); });
    });
    btn.addEventListener('click', function () {
      v._brfRefused = false;
      if (v._brfRunning) { hold(v, true); v.pause(); }
      else { hold(v, false); play(v); }
    });
    btn.hidden = false;
  });
})();

/* ── ROUND 41 · the track ───────────────────────────────────────────────────
   The row is a scroll-snap track at every width (see bundle-ritual-films.css).
   Snapping, swiping and trackpad scrolling are the browser's own; this file
   adds only what the browser will not do for itself:

   - shows the controls ONLY when the track genuinely overflows, so the four
     ticks never appear under a row where all four films already fit;
   - moves one card per press, snapped, rather than one wheel-tick;
   - keeps the ticks in step with wherever the finger left the track;
   - disables the edge arrows at the ends. `disabled`, not dimmed. An arrow
     that looks spent but still takes the tap is the exact fault PostHog caught
     customers hitting on the gallery, and it is not coming back here;
   - answers the left and right arrow keys, since the track carries tabindex=0
     and a keyboard user should move by step, not by pixel.

   Everything above is enhancement. Without this file the track still scrolls,
   still snaps and still shows every step; only the controls stay hidden, which
   is the correct thing for a control that cannot be wired. */
(function () {
  'use strict';

  var root = document.querySelector('[data-brf]');
  if (!root) return;

  var track = root.querySelector('[data-brf-track]');
  var nav = root.querySelector('[data-brf-nav]');
  if (!track || !nav) return;

  var cards = Array.prototype.slice.call(track.querySelectorAll('.brf-film'));
  var ticksWrap = nav.querySelector('[data-brf-ticks]');
  var ticks = Array.prototype.slice.call(nav.querySelectorAll('[data-brf-to]'));
  var prev = nav.querySelector('[data-brf-prev]');
  var next = nav.querySelector('[data-brf-next]');
  if (cards.length < 2 || !ticks.length || !prev || !next) return;

  var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // A snapped card's left edge lands on the track's content-box left, because
  // scroll-padding-inline and padding-inline are the same value in the CSS.
  function lead() {
    return parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
  }

  function offsetOf(card) {
    return card.getBoundingClientRect().left - track.getBoundingClientRect().left - lead();
  }

  function current() {
    var best = 0;
    var least = Infinity;
    for (var i = 0; i < cards.length; i++) {
      var d = Math.abs(offsetOf(cards[i]));
      // strictly less, so a tie between two equidistant cards keeps the earlier
      if (d < least - 1) { least = d; best = i; }
    }
    return best;
  }

  function goTo(i) {
    var card = cards[Math.max(0, Math.min(cards.length - 1, i))];
    track.scrollBy({ left: offsetOf(card), behavior: smooth ? 'smooth' : 'auto' });
  }

  // Where a tick per step stops meaning anything, and the reason this is not a
  // cosmetic detail. `scroll-snap-align: start` puts card i's snap position at
  // i x pitch, but the track only scrolls to `scrollWidth - clientWidth`. Once
  // the cards are small enough that several cards fit at once, the last snap
  // positions sit PAST that maximum and can never be landed on, so current()
  // can never return them. Measured on this build: at 1024 the track scrolls
  // 306px against a 322px pitch, so tapping the fourth tick scrolled to the end
  // and lit the SECOND; at 1265 it lit the first. Four ticks over two reachable
  // positions is a control that lies, which is the one fault the note above
  // .brf-nav swears off and the one PostHog caught customers hitting.
  //
  // Positions are monotonic, so one test settles every tick: if the card the
  // track lands on at its own end is the last card, every step is reachable and
  // the ticks are honest. Where it is not, the per-step ticks come off and the
  // arrows carry it — they step one card and disable at the ends, which is true
  // at every width. The phone, where the ticks matter most, keeps them: one
  // card fills the screen, so the pitch dwarfs the shortfall.
  function landsOnLast() {
    var max = track.scrollWidth - track.clientWidth;
    var here = track.scrollLeft;
    var best = 0;
    var least = Infinity;
    for (var i = 0; i < cards.length; i++) {
      // the scrollLeft that would snap card i to the lead edge
      var at = offsetOf(cards[i]) + here;
      var d = Math.abs(at - max);
      if (d < least - 1) { least = d; best = i; }
    }
    return best === cards.length - 1;
  }

  // The answer only changes when the track is re-laid out, and it costs five
  // getBoundingClientRect calls. Recomputed on resize, read on every frame.
  var stepsReachable = true;
  function remeasure() { stepsReachable = landsOnLast(); }

  function sync() {
    // 4px of slack: a fractional layout width is not an overflow.
    var scrolls = track.scrollWidth - track.clientWidth > 4;

    // The arrows are display:none under (hover: none) — on a touch screen the
    // swipe is the control and a 36px button is clutter. Combined with the
    // ticks coming off between about 555 and 1265, that left an iPad with a
    // nav that was shown, empty, and still claiming its 44px of the section's
    // gap: no control and a hole where one looked promised. The nav is only
    // shown when it actually holds something.
    var ticksOn = scrolls && stepsReachable;
    var arrowsOn = scrolls && window.getComputedStyle(prev).display !== 'none';
    nav.hidden = !ticksOn && !arrowsOn;
    // A focusable element that cannot scroll is a tab stop that does nothing.
    // The attribute stays in the markup for the no-JS case, where we cannot
    // know whether the track scrolls and must assume it might.
    if (scrolls) track.setAttribute('tabindex', '0');
    else track.removeAttribute('tabindex');
    if (!scrolls) return;

    if (ticksWrap) ticksWrap.hidden = !ticksOn;

    var at = current();
    for (var i = 0; i < ticks.length; i++) {
      ticks[i].setAttribute('aria-current', i === at ? 'true' : 'false');
    }
    setDisabled(prev, track.scrollLeft <= 2, next);
    setDisabled(next, track.scrollLeft >= track.scrollWidth - track.clientWidth - 2, prev);
  }

  // Disabling the control that currently holds focus drops focus to <body>, and
  // the next Tab restarts at the top of the document — a keyboard user who
  // arrows to the end of the track loses their place in the page. Focus moves
  // to the other arrow first, or to the track itself if that one is spent too.
  function setDisabled(btn, off, sibling) {
    if (off && document.activeElement === btn) {
      if (sibling && !sibling.disabled) sibling.focus();
      else if (track.hasAttribute('tabindex')) track.focus();
    }
    btn.disabled = off;
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () { queued = false; sync(); });
  }

  track.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', function () { remeasure(); schedule(); });
  if ('ResizeObserver' in window) {
    new ResizeObserver(function () { remeasure(); schedule(); }).observe(track);
  }

  prev.addEventListener('click', function () { goTo(current() - 1); });
  next.addEventListener('click', function () { goTo(current() + 1); });

  ticks.forEach(function (tick) {
    tick.addEventListener('click', function () {
      goTo(parseInt(tick.getAttribute('data-brf-to'), 10) || 0);
    });
  });

  track.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    goTo(current() + (e.key === 'ArrowRight' ? 1 : -1));
  });

  remeasure();
  sync();
})();
